import { useEffect, useState } from "react";
import {
  Box,
} from "@mui/material";
// ★cmd-po_1205 (T4-a): frame→PoModalShell / footer→PoButton へ移行
import { PoModalShell } from "./common/PoModalShell";
import { PoButton } from "./common/PoButton";
import { useSetAtom } from "jotai";
import { AppAtoms } from "../lib/store";
import {
  getSaleCompanyMatrixSetting,
  postSaleCompanyMatrixSetting,
  MatrixSettingData,
} from "../api/saleCompanyMatrixSetting";
import { poBulkTableColors } from "../lib/poTheme";

interface SaleCompanyBulkSettingModalProps {
  open: boolean;
  onClose: () => void;
  onSaveSuccess?: () => void;
  settingType: "entry" | "tossup" | "closing_partner" | "closing_category" | "collaborate";
}

export function SaleCompanyBulkSettingModal({
  open,
  onClose,
  onSaveSuccess,
  settingType,
}: SaleCompanyBulkSettingModalProps) {
  const setIsLoading = useSetAtom(AppAtoms.isLoading);
  const [matrixData, setMatrixData] = useState<MatrixSettingData | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveResult, setSaveResult] = useState<'' | 'success' | 'error'>('');

  const [entrySet, setEntrySet] = useState<Set<string>>(new Set());
  const [tossUpSet, setTossUpSet] = useState<Set<string>>(new Set());
  const [closingPartnerSet, setClosingPartnerSet] = useState<Set<string>>(new Set());
  const [closingCategorySet, setClosingCategorySet] = useState<Set<string>>(new Set());
  const [collaborateSet, setCollaborateSet] = useState<Set<string>>(new Set());

  const [initialEntrySet, setInitialEntrySet] = useState<Set<string>>(new Set());
  const [initialTossUpSet, setInitialTossUpSet] = useState<Set<string>>(new Set());
  const [initialClosingPartnerSet, setInitialClosingPartnerSet] = useState<Set<string>>(new Set());
  const [initialClosingCategorySet, setInitialClosingCategorySet] = useState<Set<string>>(new Set());
  const [initialCollaborateSet, setInitialCollaborateSet] = useState<Set<string>>(new Set());

  const SETTING_TYPE_LABEL: Record<string, string> = {
    entry: "エントリー商材",
    tossup: "トスアップ商材",
    closing_partner: "トスアップ商材 - 指定クロージングパートナー",
    closing_category: "グループ - クロージングカテゴリー",
    collaborate: "協業パートナー",
  };

  useEffect(() => {
    if (!open) {
      setMatrixData(null);
      setSaveResult('');
      return;
    }
    setLoading(true);
    setIsLoading(true);
    getSaleCompanyMatrixSetting().then((data) => {
      if (data) {
        setMatrixData(data);

        const es = new Set<string>(
          data.entry_settings.map((e) => `${e.sale_company_id}_${e.product_id}`)
        );
        setEntrySet(es);
        setInitialEntrySet(new Set(es));

        const ts = new Set<string>(
          data.toss_up_settings.map((t) => `${t.sale_company_id}_${t.product_id}`)
        );
        setTossUpSet(ts);
        setInitialTossUpSet(new Set(ts));

        const cps = new Set<string>(
          data.closing_partner_settings.map(
            (cp) => `${cp.sale_company_toss_up_product_id}_${cp.closer_company_id}`
          )
        );
        setClosingPartnerSet(cps);
        setInitialClosingPartnerSet(new Set(cps));

        const ccs = new Set<string>(
          data.closing_category_settings.map(
            (cc) => `${cc.sale_company_group_id}_${cc.category_id}`
          )
        );
        setClosingCategorySet(ccs);
        setInitialClosingCategorySet(new Set(ccs));

        const colls = new Set<string>(
          data.collaborate_settings.map((c) => {
            const a = c.sale_company_id;
            const b = c.collaborate_company_id;
            return `${Math.min(a, b)}_${Math.max(a, b)}`;
          })
        );
        setCollaborateSet(colls);
        setInitialCollaborateSet(new Set(colls));
      }
      setLoading(false);
      setIsLoading(false);
    });
  }, [open]);

  function toggleSet(
    setter: React.Dispatch<React.SetStateAction<Set<string>>>,
    key: string
  ) {
    setter((prev) => {
      const s = new Set(prev);
      if (s.has(key)) {
        s.delete(key);
      } else {
        s.add(key);
      }
      return s;
    });
  }

  async function handleSave() {
    if (!matrixData) return;
    setSaving(true);
    setMatrixData(null); // モーダルを即閉じる（CLAUDE.md: ボタン押下→モーダル閉じる）

    const entryAdd = Array.from(entrySet)
      .filter((k) => !initialEntrySet.has(k))
      .map((k) => {
        const [a, b] = k.split("_").map(Number);
        return { sale_company_id: a, product_id: b };
      });
    const entryRemove = Array.from(initialEntrySet)
      .filter((k) => !entrySet.has(k))
      .map((k) => {
        const [a, b] = k.split("_").map(Number);
        return { sale_company_id: a, product_id: b };
      });

    const tossUpAdd = Array.from(tossUpSet)
      .filter((k) => !initialTossUpSet.has(k))
      .map((k) => {
        const [a, b] = k.split("_").map(Number);
        return { sale_company_id: a, product_id: b };
      });
    const tossUpRemove = Array.from(initialTossUpSet)
      .filter((k) => !tossUpSet.has(k))
      .map((k) => {
        const [a, b] = k.split("_").map(Number);
        return { sale_company_id: a, product_id: b };
      });

    const closingPartnerAdd = Array.from(closingPartnerSet)
      .filter((k) => !initialClosingPartnerSet.has(k))
      .map((k) => {
        const [tuId, closerId] = k.split("_").map(Number);
        const tu = matrixData.toss_up_settings.find((t) => t.id === tuId)!;
        return {
          sale_company_id: tu.sale_company_id,
          product_id: tu.product_id,
          closer_company_id: closerId,
        };
      });
    const closingPartnerRemove = Array.from(initialClosingPartnerSet)
      .filter((k) => !closingPartnerSet.has(k))
      .map((k) => {
        const [tuId, closerId] = k.split("_").map(Number);
        const tu = matrixData.toss_up_settings.find((t) => t.id === tuId)!;
        return {
          sale_company_id: tu.sale_company_id,
          product_id: tu.product_id,
          closer_company_id: closerId,
        };
      });

    const closingCategoryAdd = Array.from(closingCategorySet)
      .filter((k) => !initialClosingCategorySet.has(k))
      .map((k) => {
        const [sg, cat] = k.split("_").map(Number);
        return { sale_company_group_id: sg, category_id: cat };
      });
    const closingCategoryRemove = Array.from(initialClosingCategorySet)
      .filter((k) => !closingCategorySet.has(k))
      .map((k) => {
        const [sg, cat] = k.split("_").map(Number);
        return { sale_company_group_id: sg, category_id: cat };
      });

    const collaborateAdd = Array.from(collaborateSet)
      .filter((k) => !initialCollaborateSet.has(k))
      .map((k) => {
        const [a, b] = k.split("_").map(Number);
        return { sale_company_id: a, collaborate_company_id: b };
      });
    const collaborateRemove = Array.from(initialCollaborateSet)
      .filter((k) => !collaborateSet.has(k))
      .map((k) => {
        const [a, b] = k.split("_").map(Number);
        return { sale_company_id: a, collaborate_company_id: b };
      });

    setIsLoading(true);
    const success = await postSaleCompanyMatrixSetting({
      entry: { add: entryAdd, remove: entryRemove },
      toss_up: { add: tossUpAdd, remove: tossUpRemove },
      closing_partner: { add: closingPartnerAdd, remove: closingPartnerRemove },
      closing_category: { add: closingCategoryAdd, remove: closingCategoryRemove },
      collaborate: { add: collaborateAdd, remove: collaborateRemove },
    });

    setSaving(false);
    setIsLoading(false);
    if (success) {
      onSaveSuccess?.();
      setSaveResult('success');
    } else {
      setSaveResult('error');
    }
  }

  // cmd-po_1172 件23b: catalog.html:270 の正本パターン（SVG polyline）で✓を子要素描画
  const CBX_CHECK = (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );

  const renderEntryTab = () => {
    if (!matrixData) return null;
    const sortedCompanies = [...matrixData.sale_companies].sort((a, b) => a.id - b.id);
    return (
      <>
        <thead>
          <tr>
            <th className="rowh">パートナー名</th>
            {matrixData.products.map((p) => (
              <th key={p.id}>
                {p.high_rank_company_name && <span className="sub">{p.high_rank_company_name}</span>}
                {p.product_name}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sortedCompanies.map((sc, i) => (
            <tr key={sc.id} style={{ backgroundColor: i % 2 === 0 ? poBulkTableColors.zebraOdd : poBulkTableColors.zebraEven }}>
              <td className="rowh" style={{ backgroundColor: i % 2 === 0 ? poBulkTableColors.zebraOdd : poBulkTableColors.zebraEven, whiteSpace: "nowrap" }}>
                {sc.sale_company_name}
              </td>
              {matrixData.products.map((p) => {
                const k = `${sc.id}_${p.id}`;
                return (
                  <td key={p.id}>
                    <span
                      className={`cbx${entrySet.has(k) ? " on" : ""}`}
                      data-testid={`bulk-matrix-${p.id}-${sc.id}`}
                      onClick={() => toggleSet(setEntrySet, k)}
                      role="checkbox"
                      aria-checked={entrySet.has(k)}
                    >{entrySet.has(k) && CBX_CHECK}</span>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </>
    );
  };

  const renderTossUpTab = () => {
    if (!matrixData) return null;
    const sortedCompanies = [...matrixData.sale_companies].sort((a, b) => a.id - b.id);
    return (
      <>
        <thead>
          <tr>
            <th className="rowh">パートナー名</th>
            {matrixData.products.map((p) => (
              <th key={p.id}>
                {p.high_rank_company_name && <span className="sub">{p.high_rank_company_name}</span>}
                {p.product_name}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sortedCompanies.map((sc, i) => (
            <tr key={sc.id} style={{ backgroundColor: i % 2 === 0 ? poBulkTableColors.zebraOdd : poBulkTableColors.zebraEven }}>
              <td className="rowh" style={{ backgroundColor: i % 2 === 0 ? poBulkTableColors.zebraOdd : poBulkTableColors.zebraEven, whiteSpace: "nowrap" }}>
                {sc.sale_company_name}
              </td>
              {matrixData.products.map((p) => {
                const k = `${sc.id}_${p.id}`;
                return (
                  <td key={p.id}>
                    <span
                      className={`cbx${tossUpSet.has(k) ? " on" : ""}`}
                      data-testid={`bulk-matrix-${p.id}-${sc.id}`}
                      onClick={() => toggleSet(setTossUpSet, k)}
                      role="checkbox"
                      aria-checked={tossUpSet.has(k)}
                    >{tossUpSet.has(k) && CBX_CHECK}</span>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </>
    );
  };

  const renderClosingPartnerTab = () => {
    if (!matrixData) return null;
    const sortedCompanies = [...matrixData.sale_companies].sort((a, b) => a.id - b.id);

    // パートナー名→商材名の順でソート
    const sortedTossUpSettings = [...matrixData.toss_up_settings].sort((a, b) => {
      const compA = matrixData.sale_companies.find((sc) => sc.id === a.sale_company_id);
      const compB = matrixData.sale_companies.find((sc) => sc.id === b.sale_company_id);
      const prodA = matrixData.products.find((p) => p.id === a.product_id);
      const prodB = matrixData.products.find((p) => p.id === b.product_id);
      const nameComp = (compA?.sale_company_name ?? "").localeCompare(compB?.sale_company_name ?? "", "ja");
      if (nameComp !== 0) return nameComp;
      return (prodA?.product_name ?? "").localeCompare(prodB?.product_name ?? "", "ja");
    });

    const rows: React.ReactNode[] = [];
    let lastCompanyId: number | null = null;
    let dataRowIndex = 0;

    sortedTossUpSettings.forEach((tu) => {
      if (tu.sale_company_id !== lastCompanyId) {
        lastCompanyId = tu.sale_company_id;
        const company = matrixData.sale_companies.find((sc) => sc.id === tu.sale_company_id);
        rows.push(
          <tr key={`sep_${tu.sale_company_id}`}>
            <td
              colSpan={sortedCompanies.length + 1}
              className="mx-sep"
            >
              {company?.sale_company_name ?? ""}
            </td>
          </tr>
        );
      }
      const rowBg = dataRowIndex % 2 === 0 ? poBulkTableColors.zebraOdd : poBulkTableColors.zebraEven;
      const tuProduct = matrixData.products.find((p) => p.id === tu.product_id);
      rows.push(
        <tr key={tu.id} style={{ backgroundColor: rowBg }}>
          <td className="rowh" style={{ backgroundColor: rowBg, whiteSpace: "nowrap" }}>
            {tuProduct?.product_name ?? ""}
          </td>
          {sortedCompanies.map((colSc) => {
            const isSelf = tu.sale_company_id === colSc.id;
            const k = `${tu.id}_${colSc.id}`;
            return (
              <td key={colSc.id}>
                {isSelf ? (
                  <span style={{ display: "block", textAlign: "center", lineHeight: "34px", userSelect: "none" }}>
                    -
                  </span>
                ) : (
                  <span
                    className={`cbx${closingPartnerSet.has(k) ? " on" : ""}`}
                    data-testid={`bulk-matrix-${tu.id}-${colSc.id}`}
                    onClick={() => toggleSet(setClosingPartnerSet, k)}
                    role="checkbox"
                    aria-checked={closingPartnerSet.has(k)}
                  >{closingPartnerSet.has(k) && CBX_CHECK}</span>
                )}
              </td>
            );
          })}
        </tr>
      );
      dataRowIndex++;
    });

    return (
      <>
        <thead>
          <tr>
            <th className="rowh">商材名</th>
            {sortedCompanies.map((sc) => (
              <th key={sc.id}>{sc.sale_company_name}</th>
            ))}
          </tr>
        </thead>
        <tbody>{rows}</tbody>
      </>
    );
  };

  const renderClosingCategoryTab = () => {
    if (!matrixData) return null;
    const sortedCompanies = [...matrixData.sale_companies].sort((a, b) => a.id - b.id);
    const companyOrderMap = new Map(sortedCompanies.map((sc, idx) => [sc.id, idx]));
    const sortedGroups = [...matrixData.sale_company_groups].sort((a, b) => {
      const orderA = companyOrderMap.get(a.sale_company_id) ?? 0;
      const orderB = companyOrderMap.get(b.sale_company_id) ?? 0;
      return orderA - orderB;
    });

    const rows: React.ReactNode[] = [];
    let lastCompanyId: number | null = null;
    let dataRowIndex = 0;

    sortedGroups.forEach((sg) => {
      if (sg.sale_company_id !== lastCompanyId) {
        lastCompanyId = sg.sale_company_id;
        const company = matrixData.sale_companies.find(
          (sc) => sc.id === sg.sale_company_id
        );
        rows.push(
          <tr key={`sep_${sg.sale_company_id}`}>
            <td
              colSpan={matrixData.categories.length + 1}
              className="mx-sep"
            >
              {company?.sale_company_name ?? ""}
            </td>
          </tr>
        );
      }
      const rowBg = dataRowIndex % 2 === 0 ? poBulkTableColors.zebraOdd : poBulkTableColors.zebraEven;
      rows.push(
        <tr key={sg.id} style={{ backgroundColor: rowBg }}>
          <td className="rowh" style={{ backgroundColor: rowBg, whiteSpace: "nowrap" }}>
            {sg.sale_company_group_name}
          </td>
          {matrixData.categories.map((cat) => {
            const k = `${sg.id}_${cat.id}`;
            return (
              <td key={cat.id}>
                <span
                  className={`cbx${closingCategorySet.has(k) ? " on" : ""}`}
                  onClick={() => toggleSet(setClosingCategorySet, k)}
                  role="checkbox"
                  aria-checked={closingCategorySet.has(k)}
                >{closingCategorySet.has(k) && CBX_CHECK}</span>
              </td>
            );
          })}
        </tr>
      );
      dataRowIndex++;
    });

    return (
      <>
        <thead>
          <tr>
            <th className="rowh">グループ名</th>
            {matrixData.categories.map((cat) => (
              <th key={cat.id}>{cat.category_name}</th>
            ))}
          </tr>
        </thead>
        <tbody>{rows}</tbody>
      </>
    );
  };

  const renderCollaborateTab = () => {
    if (!matrixData) return null;
    const sortedCompanies = [...matrixData.sale_companies].sort((a, b) => a.id - b.id);
    return (
      <>
        <thead>
          <tr>
            <th className="rowh">パートナー名</th>
            {sortedCompanies.map((col) => (
              <th key={col.id}>{col.sale_company_name}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sortedCompanies.map((row, i) => (
            <tr key={row.id} style={{ backgroundColor: i % 2 === 0 ? poBulkTableColors.zebraOdd : poBulkTableColors.zebraEven }}>
              <td className="rowh" style={{ backgroundColor: i % 2 === 0 ? poBulkTableColors.zebraOdd : poBulkTableColors.zebraEven, whiteSpace: "nowrap" }}>
                {row.sale_company_name}
              </td>
              {sortedCompanies.map((col) => {
                const isSelf = row.id === col.id;
                const isMirror = row.id > col.id;
                const isDisabled = isSelf || isMirror;
                const canonicalKey = `${Math.min(row.id, col.id)}_${Math.max(row.id, col.id)}`;
                const isChecked = collaborateSet.has(canonicalKey);
                return (
                  <td key={col.id}>
                    {isDisabled ? (
                      <span style={{ display: "block", textAlign: "center", lineHeight: "34px", userSelect: "none" }}>
                        -
                      </span>
                    ) : (
                      <span
                        className={`cbx${isChecked ? " on" : ""}`}
                        onClick={() => toggleSet(setCollaborateSet, canonicalKey)}
                        role="checkbox"
                        aria-checked={isChecked}
                      >{isChecked && CBX_CHECK}</span>
                    )}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </>
    );
  };

  // ★cmd-po_1205 (T4-a): footer を PoModalShell の footer スロットへ status 別に分離
  const footerNode =
    saveResult !== '' ? (
      <PoButton
        variant="secondary"
        data-testid="bulk-setting-close-button"
        onClick={() => {
          setSaveResult('');
          onClose();
        }}
      >
        閉じる
      </PoButton>
    ) : (
      <>
        <PoButton
          variant="secondary"
          onClick={onClose}
          data-testid="bulk-setting-cancel-button"
        >
          キャンセル
        </PoButton>
        <PoButton
          variant="primary"
          onClick={handleSave}
          data-testid="bulk-setting-save-button"
        >
          保存
        </PoButton>
      </>
    );

  return (
    <PoModalShell
      open={open && !loading && (!!matrixData || saveResult !== '')}
      title={`${SETTING_TYPE_LABEL[settingType] ?? "一括設定"} 一括設定`}
      onClose={onClose}
      width="w760"
      testidPrefix="bulk-setting"
      frameTestId="bulk-setting-modal"
      bodySx={{ p: 0, gap: 0 }}
      footer={footerNode}
    >
        {saveResult !== '' ? (
          <Box className="ce-body" sx={{ display: "flex", flexDirection: "column", gap: "var(--sp-3)", pt: "var(--sp-4)" }}>
            <Box className={`result-state${saveResult === "success" ? " -success" : " -failed"}`}>
              <Box component="span" className="rs-icon">
                {saveResult === "success" ? (
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none"><path d="M20 6L9 17l-5-5" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                ) : (
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none"><path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                )}
              </Box>
              <Box component="span" className="rs-title">
                {saveResult === 'success' ? '更新が完了しました。' : '更新に失敗しました。'}
              </Box>
              {saveResult !== 'success' && (
                <Box component="span" className="rs-desc">再度お試しください。</Box>
              )}
            </Box>
          </Box>
        ) : (
          <Box className="ce-body" sx={{ display: "flex", flexDirection: "column", gap: "var(--sp-3)", pt: "var(--sp-4)", flex: 1, minHeight: 0 }}>
            <Box sx={{ maxHeight: "55vh", overflow: "auto", width: "100%" }}>
              <table className="mx-table">
                {settingType === "entry" && renderEntryTab()}
                {settingType === "tossup" && renderTossUpTab()}
                {settingType === "closing_partner" && renderClosingPartnerTab()}
                {settingType === "closing_category" && renderClosingCategoryTab()}
                {settingType === "collaborate" && renderCollaborateTab()}
              </table>
            </Box>
            <Box component="span" className="po-helper">
              チェックを入れた組み合わせが有効になります。保存後に反映されます。
            </Box>
          </Box>
        )}
    </PoModalShell>
  );
}
